import { useMutation, useQueryClient } from '@tanstack/react-query';
import { diContainer } from 'src/inversify.config';
import AddressesRepository from 'src/features/addresses/data/AddressesRepository';
import {
  CreateAddressParams,
  DeleteAddressParams,
  SetMainAddressParams,
} from 'features/addresses/domain/AddressesRepositoryInterface';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import { Address } from 'src/types/common';
import { useCartContext } from 'src/context/CartProvider/useCartContext';
import useAddressList from 'src/features/addresses/presentation/hooks/useAddressList';
import DeleteAddressUseCase from 'src/features/addresses/domain/UseCases/DeleteAddressUseCase';
import useTrackAddressEvents from 'src/features/addresses/presentation/hooks/useTrackAddressEvents';
import {
  DeleteAddressResponse,
  EditAddressRequestParams,
} from 'src/features/addresses/data/service/addressesService.types';
import { useCurrentUserQuery } from 'src/features/auth/presentation/hooks/useCurrentUserQuery';
import { AxiosError } from 'axios';

const addressesRepository =
  diContainer.get<AddressesRepository>(AddressesRepository);
const deleteAddressUseCase =
  diContainer.get<DeleteAddressUseCase>(DeleteAddressUseCase);

export default function useAddressMutations() {
  const { user } = useAuthContext();
  const { createUserCart } = useCartContext();
  const {
    trackAddressCreate,
    trackAddressSelect,
    trackCreateAddressError,
    trackAddressEdit,
    trackEditAddressError,
  } = useTrackAddressEvents();
  const queryClient = useQueryClient();

  const updateUserState = () => {
    queryClient.invalidateQueries({
      queryKey: useCurrentUserQuery.getQueryKey(),
    });
  };

  const handleMutationsSuccess = async (data: Address) => {
    await createUserCart?.({
      storeSlugName: data?.store_data?.slug_name || undefined,
      addressId: data.pk,
    });
    updateUserState();
  };

  const setMainAddress = useMutation({
    mutationFn: (params: SetMainAddressParams) =>
      addressesRepository.setMainAddress(params),
    onMutate: () => {
      const currentMainAddress = user?.main_address;

      return currentMainAddress;
    },
    onSuccess: async (data) => {
      await handleMutationsSuccess(data);
      trackAddressSelect(data);
    },
    onError: (_error, variables, context) => {
      context &&
        addressesRepository.setMainAddress({
          ...variables,
          addressId: context?.pk,
        });
      updateUserState();
    },
    mutationKey: ['setMainAddress'],
  });

  const createAddress = useMutation({
    mutationFn: (params: CreateAddressParams) =>
      addressesRepository.createAddress(params),
    onMutate: () => {
      const currentMainAddress = user?.main_address;

      return currentMainAddress;
    },
    onSuccess: async (data) => {
      await handleMutationsSuccess(data);
      trackAddressCreate(data);
    },
    onError: (_error, variables, context) => {
      context && updateUserState();
      context &&
        addressesRepository.setMainAddress({
          ...variables,
          addressId: context?.pk,
        });
      trackCreateAddressError(variables);
    },
    mutationKey: ['createAddress'],
  });

  const editAddress = useMutation<
    Address,
    AxiosError<string[]>,
    EditAddressRequestParams,
    unknown
  >({
    mutationFn: (params: EditAddressRequestParams) =>
      addressesRepository.editAddress(params),
    onSuccess: async (data) => {
      await handleMutationsSuccess(data);
      trackAddressEdit(data);
    },
    onError: (_error, variables) => {
      trackEditAddressError(variables);
    },
    mutationKey: ['editAddress'],
  });

  const deleteAddress = useMutation<
    DeleteAddressResponse,
    AxiosError<string[]>,
    DeleteAddressParams,
    unknown
  >({
    mutationFn: (params: DeleteAddressParams) =>
      deleteAddressUseCase.execute(params),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: useAddressList.getQueryKey(),
      });
    },
    mutationKey: ['deleteAddress'],
  });

  return {
    setMainAddress,
    createAddress,
    deleteAddress,
    editAddress,
  };
}
